import React from 'react';
import { FormattedMessage } from 'react-intl';

import Link from '../Link';

import { Caption, Subtitle2 } from '../Typography';

import formatDate from '../../app/helpers/dateFormat';
import camelCaseToKebabCase from '../../app/helpers/camelCaseToKebabCase';
import {
  ImmunotherapyStepIds,
  NutritionAdviceStepIds,
} from '../../app/constants/treatment';

import styles from './index.module.scss';

export interface TreatmentStep {
  id?: string;
  bookingData?: {
    bookingCompleted?: boolean;
  };
}

export interface TreatmentCardProps {
  iconUrl: string;
  type: string;
  activeStepId: string;
  testCode: string;
  doctorName: string;
  startDate: string;
  id: string;
  steps: TreatmentStep[];
}

const getStatus = (activeStepId: string, steps: TreatmentStep[]): string => {
  const activeStep = steps.find((step) => step?.id === activeStepId);

  if (activeStep?.bookingData?.bookingCompleted) {
    return 'dashboard.waiting-for-consultation-with-nutritionist';
  }

  switch (activeStepId) {
    case ImmunotherapyStepIds.BOOK_PREPARATORY_CONSULTATION:
      return 'dashboard.book-consultation';

    case ImmunotherapyStepIds.BOOK_FIRST_TIME_INTAKE_MEDICATION_ONE:
    case ImmunotherapyStepIds.BOOK_FIRST_TIME_INTAKE_MEDICATION_TWO:
    case ImmunotherapyStepIds.BOOK_FIRST_TIME_INTAKE_MEDICATION_THREE:
      return 'dashboard.book-first-intake';

    case ImmunotherapyStepIds.START_TAKING_MEDICATION_ONE:
    case ImmunotherapyStepIds.START_TAKING_MEDICATION_TWO:
    case ImmunotherapyStepIds.START_TAKING_MEDICATION_THREE:
      return 'dashboard.start-medication';

    case ImmunotherapyStepIds.CHECK_IN_MEDICATION_ONE:
    case ImmunotherapyStepIds.CHECK_IN_MEDICATION_TWO:
    case ImmunotherapyStepIds.CHECK_IN_MEDICATION_THREE:
      return 'dashboard.book-doctor-check-in';

    case ImmunotherapyStepIds.GET_ONE_YEAR_MEDICATION_ONE:
    case ImmunotherapyStepIds.GET_ONE_YEAR_MEDICATION_TWO:
    case ImmunotherapyStepIds.GET_ONE_YEAR_MEDICATION_THREE:
      return 'dashboard.get-medication';

    case NutritionAdviceStepIds.ANSWER_HEALTH_QUESTIONNAIRE:
      return 'dashboard.start-health-questionnaire';
    case NutritionAdviceStepIds.COMPLETE_FOOD_JOURNAL:
      return 'dashboard.complete-food-journal';
    case NutritionAdviceStepIds.BOOK_CONSULTATION_WITH_NUTRITIONIST_ONE:
      return 'dashboard.book-consultation-with-nutritionist';
    case NutritionAdviceStepIds.BOOK_CONSULTATION_WITH_NUTRITIONIST_TWO:
    case NutritionAdviceStepIds.BOOK_CONSULTATION_WITH_NUTRITIONIST_THREE:
    case NutritionAdviceStepIds.BOOK_CONSULTATION_WITH_NUTRITIONIST_FOUR:
    case NutritionAdviceStepIds.BOOK_CONSULTATION_WITH_NUTRITIONIST_FIVE:
    case NutritionAdviceStepIds.BOOK_CONSULTATION_WITH_NUTRITIONIST_SIX:
    case NutritionAdviceStepIds.BOOK_CONSULTATION_WITH_NUTRITIONIST_SEVEN:
      return 'dashboard.book-follow-on-consultation';
    case NutritionAdviceStepIds.RECEIVE_NUTRITION_PLAN_ONE:
    case NutritionAdviceStepIds.RECEIVE_NUTRITION_PLAN_TWO:
    case NutritionAdviceStepIds.RECEIVE_NUTRITION_PLAN_THREE:
    case NutritionAdviceStepIds.RECEIVE_NUTRITION_PLAN_FOUR:
    case NutritionAdviceStepIds.RECEIVE_NUTRITION_PLAN_FIVE:
    case NutritionAdviceStepIds.RECEIVE_NUTRITION_PLAN_SIX:
    case NutritionAdviceStepIds.RECEIVE_NUTRITION_PLAN_SEVEN:
      return 'dashboard.receive-nutrition-plan';

    default:
      return '';
  }
};

const getButtonText = (activeStepId: string) => {
  if (
    activeStepId === ImmunotherapyStepIds.BOOK_PREPARATORY_CONSULTATION ||
    activeStepId === NutritionAdviceStepIds.ANSWER_HEALTH_QUESTIONNAIRE
  ) {
    return 'dashboard.start-treatment';
  } else {
    return 'dashboard.continue-treatment';
  }
};

const TreatmentCard: React.FC<TreatmentCardProps> = ({
  iconUrl,
  type,
  activeStepId,
  testCode,
  doctorName,
  startDate,
  id,
  steps,
}) => {
  const status = getStatus(activeStepId, steps);
  const buttonText = getButtonText(activeStepId);

  return (
    <div className={styles.wrapper}>
      <div className={styles.cardWrapper}>
        <div className={styles.imageContainer}>
          <div className={styles.imageWrapper}>
            <img className={styles.image} src={iconUrl} alt='product' />
          </div>
        </div>
        <div className={styles.rightSide}>
          <div className={styles.textContentWrapper}>
            <div className={styles.titleWrapper}>
              <Subtitle2 className={styles.title}>
                <FormattedMessage
                  id={`treatment.${camelCaseToKebabCase(type)}`}
                  defaultMessage=''
                />
              </Subtitle2>
              <Caption element='p' className={styles.testCode}>
                {testCode}
              </Caption>

              {status && (
                <Caption element='p' className={styles.description}>
                  <FormattedMessage id={status} defaultMessage='' />
                </Caption>
              )}
            </div>

            {doctorName && (
              <div className={styles.testInfoName}>
                <Caption element='p' className={styles.testInfoLabel}>
                  <FormattedMessage
                    id='dashboard.assigned-doctor'
                    defaultMessage='Assigned doctor: '
                  />
                </Caption>
                <Caption element='p' className={styles.testInfoData}>
                  {doctorName}
                </Caption>
              </div>
            )}

            {startDate && (
              <div className={styles.testInfoName}>
                <Caption element='p' className={styles.testInfoLabel}>
                  <FormattedMessage
                    id='dashboard.start-date'
                    defaultMessage='Start date: '
                  />
                </Caption>
                <Caption element='p' className={styles.testInfoData}>
                  {formatDate(startDate)}
                </Caption>
              </div>
            )}
          </div>

          {buttonText && (
            <Link
              size='small'
              url={`/treatment/${id}`}
              className={styles.button}
            >
              <FormattedMessage id={buttonText} defaultMessage='' />
            </Link>
          )}
        </div>
      </div>
    </div>
  );
};

export default TreatmentCard;
